import React, { FormEvent, ChangeEvent } from 'react';
import classnames from 'classnames';
import styles from './input-field.css';

interface InputFieldProps {
  /** DOM is */
  id?: string;
  /** Disables input field disallowing interaction */
  disabled?: boolean;
  /** Auto focus on input */
  autofocus?: boolean;
  /** Displays placeholder text inside the input field */
  placeholder?: string;
  /** Adds required attribute */
  required?: boolean;
  /** Adds additional classes to the component. */
  className?: string;
  /** Sets the input field type */
  type?: 'text' | 'password' | 'number';
  /** Name added to the input field in the HTML Markup */
  name?: string;
  /** Input Field value (it should be accompanied by an onChange function) */
  value?: string;
  /** Provides visual elements for usage on a dark environment */
  darkEnv?: boolean;
  /** Provides visual styles for a smaller input field */
  mini?: boolean;
  /** Provides visual styles for a minimal ui input */
  minimal?: boolean;
  /** Callback when typed on */
  onChange?: (ev: FormEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement>) => void;
  /** Callback when input looses focus */
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  /** Callback when a key is pressed */
  onKeyDown?: (ev: React.KeyboardEvent) => void;
  /** Minimum number value */
  min?: number;
  /** Maximum number value */
  max?: number;
  /** Specifies the interval between legal numbers */
  step?: number;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Input field component
 *
 * @param {InputFieldProps} props
 *
 * @returns JSX.ELEMENT
 */
export const InputField = ({
  id,
  placeholder,
  className,
  name,
  darkEnv,
  onChange,
  onBlur,
  onKeyDown,
  min,
  max,
  step,
  'data-testid': dataTestId,
  value = '',
  required,
  disabled = false,
  autofocus = false,
  type = 'text',
  mini = false,
  minimal = false
}: InputFieldProps) => {
  const inputClasses = classnames(styles.input, className, {
    [styles.dark]: darkEnv,
    [styles.disabled]: disabled,
    [styles.mini]: mini,
    [styles.minimal]: minimal
  });

  return (
    <input
      className={inputClasses}
      type={type}
      required={required}
      disabled={disabled}
      autoFocus={autofocus}
      placeholder={placeholder}
      name={name}
      value={value}
      min={min}
      max={max}
      step={step}
      {...(!disabled && typeof onChange === 'function' ? { onChange } : {})}
      {...(!disabled && typeof onBlur === 'function' ? { onBlur } : {})}
      {...(!disabled && typeof onKeyDown === 'function' ? { onKeyDown } : {})}
      data-testid={dataTestId}
      id={id}
    />
  );
};

export default InputField;
